/**
 * helper util function
 *
 */

import { Intent, Scheme, UniversalLink } from '../types';
import { stringify, StringifyOptions } from 'query-string';

type Deleted<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

/**
 * 删除object中的key
 */
function deleteKeys<T, K extends keyof T>(object: T, key: K) {
  const clone = Object.assign({}, object) as T;
  delete clone[key];
  return clone as Deleted<T, K>;
}

/**
 * 生成scheme
 * @param scheme - scheme对象
 * @param stringifyOptions - 使用query-string库，stringify的选项
 *
 */
export function generateScheme(scheme: Scheme, stringifyOptions?: StringifyOptions): string {
  let schemeURL = scheme.protocol;
  const { host = '', port = '', path = '', params: query = '', anchor = '' } = scheme;

  let queryPart;
  if (typeof query === 'string') {
    queryPart = query ? `?${query}` : '';
  } else {
    // const str = Object.keys(query).map(key => `${key}=${query[key]}`).join('&');
    const str = stringify(query, stringifyOptions);
    queryPart = str ? `?${str}` : '';
  }

  let hostPort = '';
  if (port) {
    hostPort = `${host}:${port}/`;
  } else {
    hostPort = host ? `${host}/` : '';
  }

  const anchorPart = anchor ? `#${anchor}` : '';

  const pathPart = path[0] === '/' ? path.slice(1) : path;

  schemeURL = `${scheme.protocol}://${hostPort}${pathPart}${queryPart}${anchorPart}`;
  return schemeURL;
}

/**
 * 生成intent
 * @param intent - intent对象
 * @param scheme - scheme对象
 *
 */
export function generateIntent(intent: Intent, scheme: Scheme, stringifyOptions?: StringifyOptions): string {
  // 删除fallback参数
  const intentParams = deleteKeys(intent, 'fallback');

  // 添加S.browser_fallback_url， 其实就是换了个名字
  const intentFallbackParam = intent.fallback ? `;S.browser_fallback_url=${encodeURIComponent(intent.fallback)}` : '';

  const intentTailParam =    (Object.keys(intentParams) as Array<keyof typeof intentParams>)
    .map(key => `${key}=${intentParams[key]}`)
    .join(';') + intentFallbackParam;

  /** intent 尾部 */
  const intentTail = `#Intent;${intentTailParam};end;`;

  const urlPath: string = generateScheme(scheme, stringifyOptions).match(/^.*?:\/\/(.*)$/)?.[1] ?? '';

  return `intent://${urlPath}${intentTail}`;
}

/**
 * 生成universalLink
 * @param universalLink - UniversalLink对象
 */
export function generateUniversalLink(universalLink: UniversalLink): string {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { host, params, path, pathKey: paramKey } = universalLink;

  // TODO:
  return '';
}

/**
 *
 *
 * [RFC3986-Appendix B. Parsing a URI Reference with a Regular Expression](https://tools.ietf.org/html/rfc3986#appendix-B)
 *
 * 解析url路径
 * - `example/path` 下，只会识别出path，不会识别到host
 * - `/example/path` 下，只会识别出path
 * - `//example/path` 下，会识别example为`"host"`, path为`"path"`
 * - `//` 下，会识别出host为`""`
 * - qq:// 下，会识别出scheme为`qq`, host为`""`
 *
 */
export function parseUrl(url: string): Partial<Omit<Scheme, 'params'> & { params: string }> {
  const matches = url.match(/^(([^:/?#]+):)?(\/\/([^/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?/);
  const hostPort = matches?.[4];
  const hostPortMatch = hostPort?.match(/([^:]*)(:(\d+))?/);
  const host = hostPortMatch?.[1];
  const port = hostPortMatch?.[3];
  return {
    protocol: matches?.[2],
    host,
    port,
    path: matches?.[5],
    params: matches?.[7],
    anchor: matches?.[9],
  };
}

/**
 * 将参数统一格式化为数组形式
 *
 * 此函数的目的是将某些支持单个参数、数组参数的选项统一转化为数组形式的参数
 *
 */
export function formatToArray<T>(config?: T[] | T): T[] {
  const arr: T[] = [];
  if (Array.isArray(config)) {
    arr.push(...config);
  } else if (config) {
    arr.push(config);
  }
  return arr;
}
